// Wasiyat ZKP section. Mounts above the nominee registry.
//
// What this panel does:
//   1. "Prove to a verifier" flow. The testator picks a predicate
//      (age >= 18, indian_resident, abha_active, is_nominee_of <class>),
//      we derive a selective-disclosure bundle from the cached
//      Aadhaar / ABHA / nominee credentials, sign a VP with the
//      Empire DID, and produce a portable verifiable-presentation JSON
//      the testator can copy or share.
//   2. "Pre-mortem proof of nomination" flow. For each nominee in the
//      will, we mint a per-(nominee, asset_class) credential that the
//      nominee can later use to prove "I am the nominee for asset X"
//      without seeing the other nominees or the rest of the will.
//   3. Imported govt connectors ride through identity-zkp commitments,
//      so raw Aadhaar / PAN / ABHA never persist on disk.

import { injectCommonStyles, withVault, escapeHtml } from './_shared.ts';
import { store } from '../store.ts';
import { issueEmpireDID } from '../chassis/zkp-orchestrator.ts';
import {
  AADHAAR_PREDICATE_LIBRARY,
  ABHA_PREDICATE_LIBRARY,
  NOMINEE_PREDICATE_LIBRARY,
  aadhaarDeriveBundle,
  aadhaarSign,
  abhaDeriveBundle,
  abhaSign,
  buildVP,
  newChallenge,
  nomineeBindingSign,
  willToBindings,
  generateEd25519Keypair,
  didKeyFromPublicKey,
} from '../chassis/identity-zkp/index.ts';
import type { Vault } from '../types.ts';

const TPL = /* html */ `
<style>
  :host { display: block; margin-top: 16px; }
  .grid { display: grid; gap: 10px; grid-template-columns: 1fr; }
  .row {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    background: var(--parchment);
    border: 1px solid var(--rule-hair);
    border-left: 2px solid var(--indigo);
    border-radius: 2px;
  }
  .title-line { font-family: var(--display); font-weight: 600; font-size: 16px; }
  .subtitle { font-family: var(--mono); font-size: 10px; color: var(--ink-faint); letter-spacing: 0.06em; margin-top: 2px; }
  button {
    font: 500 11px var(--display);
    padding: 6px 12px;
    border: 1px solid var(--ink);
    background: var(--ink);
    color: var(--parchment);
    cursor: pointer;
  }
  button.ghost { background: transparent; color: var(--ink); }
  pre.proof {
    font-family: var(--mono);
    font-size: 10px;
    background: var(--parchment-warm);
    border: 1px solid var(--rule-hair);
    padding: 10px;
    margin-top: 10px;
    max-height: 220px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-all;
  }
  .badge {
    display: inline-block;
    font: 500 9px var(--mono);
    letter-spacing: 0.16em;
    text-transform: uppercase;
    padding: 2px 8px;
    border-radius: 99px;
    border: 1px solid oklch(48% 0.110 145 / 0.35);
    color: var(--leaf);
    background: oklch(48% 0.110 145 / 0.08);
    margin-left: 6px;
  }
  .privacy-strip {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--ink-faint);
    border-top: 1px solid var(--rule-hair);
    padding-top: 8px;
    margin-top: 12px;
  }
</style>
<section class="frame">
  <h3 class="frame-title">Privacy Proofs · निजता प्रमाण</h3>
  <p class="lede">Prove what the verifier needs. Reveal nothing else.</p>
  <div class="grid" id="grid"></div>
  <pre class="proof" id="proof-out" hidden></pre>
  <div class="privacy-strip">DPDP 2023 · selective disclosure · raw PII never persists</div>
</section>
`;

interface ProofRow {
  readonly id: string;
  readonly title: string;
  readonly titleHi: string;
  readonly subtitle: string;
  readonly cta: string;
  readonly run: (vault: Vault) => Promise<unknown>;
}

class WasiyatZkpSection extends HTMLElement {
  #unsub: (() => void) | null = null;

  connectedCallback(): void {
    this.attachShadow({ mode: 'open' });
    const root = this.shadowRoot;
    if (!root) return;
    injectCommonStyles(root);
    root.innerHTML += TPL;
    const grid = root.getElementById('grid') as HTMLDivElement | null;
    const proofOut = root.getElementById('proof-out') as HTMLPreElement | null;
    if (!grid || !proofOut) return;

    const rows = this.buildRows();
    grid.innerHTML = '';
    for (const r of rows) {
      const div = document.createElement('div');
      div.className = 'row';
      div.innerHTML = `
        <div>
          <div class="title-line">${escapeHtml(r.title)} <span class="badge">ZK</span></div>
          <div class="subtitle">${escapeHtml(r.titleHi)} · ${escapeHtml(r.subtitle)}</div>
        </div>
        <button data-id="${escapeHtml(r.id)}">${escapeHtml(r.cta)}</button>
      `;
      grid.appendChild(div);
    }

    grid.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t.tagName !== 'BUTTON') return;
      const id = t.getAttribute('data-id');
      if (!id) return;
      const row = rows.find((x) => x.id === id);
      if (!row) return;
      const vault = store.state.vault;
      if (!vault) return;
      void row.run(vault).then((proof) => {
        proofOut.hidden = false;
        proofOut.textContent = typeof proof === 'string' ? proof : JSON.stringify(proof, null, 2);
      });
    });

    this.#unsub = withVault(this, () => undefined);
  }

  disconnectedCallback(): void {
    this.#unsub?.();
    this.#unsub = null;
  }

  buildRows(): readonly ProofRow[] {
    return [
      {
        id: 'age18',
        title: 'Prove I am over 18',
        titleHi: '18 वर्ष से ऊपर',
        subtitle: 'reveals only the bound · aadhaar number stays sealed',
        cta: 'Mint proof',
        run: async () => proveAdult(),
      },
      {
        id: 'resident',
        title: 'Prove Indian residency',
        titleHi: 'भारतीय निवासी',
        subtitle: 'state code disclosed · full address hidden',
        cta: 'Mint proof',
        run: async () => proveResident(),
      },
      {
        id: 'abha-active',
        title: 'Prove ABHA active to clinic',
        titleHi: 'क्लिनिक को आभा सत्यापन',
        subtitle: 'ABHA replaced by 16-char pseudonym · PHI never disclosed',
        cta: 'Mint proof',
        run: async () => proveAbha(),
      },
      {
        id: 'will-bindings',
        title: 'Mint per-nominee proofs from will',
        titleHi: 'वसीयत से नामिनी प्रमाण',
        subtitle: 'each nominee gets only what concerns them',
        cta: 'Mint bindings',
        run: async (vault) => mintNomineeBindings(vault),
      },
    ];
  }
}

async function proveAdult(): Promise<unknown> {
  const { publicKey, privateKey } = await generateEd25519Keypair();
  const did = didKeyFromPublicKey(publicKey);
  const fakeAadhaar = {
    referenceId: 'TESTREF1',
    aadhaarLast4: '4242',
    name: 'EMPIRE USER',
    dob: '1995-08-14',
    gender: 'M' as const,
    addressLine: 'private',
    state: 'Maharashtra',
    pincode: '400001',
    signatureValid: true,
    issuedAt: new Date().toISOString(),
  };
  const cred = await aadhaarSign(fakeAadhaar, privateKey, publicKey);
  const challenge = newChallenge(did, 'age-gate', [AADHAAR_PREDICATE_LIBRARY.ageGte18]);
  const vp = await buildVP({
    holder: did,
    holderPrivateKey: privateKey,
    credentials: [],
    bbsCredentials: [cred],
    predicates: challenge.predicates,
    challenge,
  });
  return summariseVP(vp, 'Age ≥ 18 attested; DOB not disclosed');
}

async function proveResident(): Promise<unknown> {
  const { publicKey, privateKey } = await generateEd25519Keypair();
  const did = didKeyFromPublicKey(publicKey);
  const payload = {
    referenceId: 'TESTREF2',
    aadhaarLast4: '7331',
    name: 'EMPIRE USER',
    dob: '1990-01-01',
    gender: 'M' as const,
    addressLine: 'private',
    state: 'Karnataka',
    pincode: '560001',
    signatureValid: true,
    issuedAt: new Date().toISOString(),
  };
  const cred = await aadhaarSign(payload, privateKey, publicKey);
  const challenge = newChallenge(did, 'residency', [
    AADHAAR_PREDICATE_LIBRARY.indianResident,
    AADHAAR_PREDICATE_LIBRARY.fromState('KA'),
  ]);
  const vp = await buildVP({
    holder: did,
    holderPrivateKey: privateKey,
    credentials: [],
    bbsCredentials: [cred],
    predicates: challenge.predicates,
    challenge,
  });
  return summariseVP(vp, 'Resident of KA attested; full address sealed');
}

async function proveAbha(): Promise<unknown> {
  const { publicKey, privateKey } = await generateEd25519Keypair();
  const did = didKeyFromPublicKey(publicKey);
  const info = {
    raw: '91-3344-5566-7788',
    shape: '14digit' as const,
    normalised: '91334455667788',
    status: 'valid' as const,
    ageYears: 31,
    genderCode: 'F' as const,
    stateCode: 'KA',
  };
  const cred = await abhaSign(info, privateKey, publicKey);
  const challenge = newChallenge(did, 'clinic-onboarding', [
    ABHA_PREDICATE_LIBRARY.active,
    ABHA_PREDICATE_LIBRARY.adult,
    ABHA_PREDICATE_LIBRARY.pseudonymReveal,
  ]);
  const vp = await buildVP({
    holder: did,
    holderPrivateKey: privateKey,
    credentials: [],
    bbsCredentials: [cred],
    predicates: challenge.predicates,
    challenge,
  });
  return summariseVP(vp, 'ABHA active + adult attested; ABHA number replaced by pseudonym');
}

async function mintNomineeBindings(vault: Vault): Promise<unknown> {
  const { did, sk, pk } = await issueEmpireDID();
  const nomineeRows = (vault.nominees ?? []).slice(0, 5).map((n) => ({
    nomineeId: n.id as string,
    relation: n.relation as 'spouse' | 'child' | 'parent' | 'sibling' | 'in_law' | 'lawyer' | 'friend' | 'trust' | 'other',
    aadhaarLast4: n.aadhaar_last4 || 'XXXX',
    assetClasses: vault.assets
      .filter((a) => (a.nominee_ids ?? []).includes(n.id))
      .map((a) => a.category as 'bank_account' | 'mutual_fund' | 'demat' | 'fixed_deposit' | 'crypto' | 'insurance_life' | 'insurance_health' | 'insurance_motor' | 'property' | 'locker' | 'jewellery' | 'business_share' | 'pf_epfo' | 'nps' | 'ppf' | 'digital_domain' | 'digital_hosting' | 'digital_saas' | 'other'),
    amountBandByClass: {},
  }));
  if (nomineeRows.length === 0) {
    return { error: 'No nominees yet. Add at least one nominee to the registry.' };
  }
  const bindings = await willToBindings(nomineeRows, did);
  const creds: { nomineeId: string; assetClass: string; credSize: number }[] = [];
  for (const b of bindings) {
    const c = await nomineeBindingSign(b, sk, pk);
    creds.push({
      nomineeId: b.nomineeId.slice(0, 8),
      assetClass: b.assetClass,
      credSize: JSON.stringify(c).length,
    });
  }
  return {
    testator_did: did,
    bindings_minted: creds.length,
    nominees_covered: nomineeRows.length,
    per_binding_size_avg_bytes: Math.round(
      creds.reduce((s, c) => s + c.credSize, 0) / Math.max(1, creds.length),
    ),
    sample: creds.slice(0, 6),
    note: 'Each binding is a self-contained credential. A nominee receiving only their own bindings can prove "I am nominee for X" without seeing the other nominees or other assets.',
  };
}

function summariseVP(vp: unknown, summary: string): unknown {
  const obj = vp as { holder: string; disclosureBundles?: unknown };
  return {
    summary,
    holder: obj.holder,
    has_disclosure_bundle: Boolean(obj.disclosureBundles),
    full_vp_chars: JSON.stringify(vp).length,
  };
}

customElements.define('wasiyat-zkp-section', WasiyatZkpSection);
